import React, { useEffect, useState, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  styled,
  Paper,
  Typography,
  Grid,
  CssBaseline,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiClient from '../api/apiClient';
import extractApiError from '../api/extractApiError';
import ApplicationEditStepOne from '../components/Application/EditeApplication/ApplicationEditStepOne';
import ApplicationEditStepThree from '../components/Application/EditeApplication/ApplicationEditStepThree';
import ApplicationEditStepFour from '../components/Application/EditeApplication/ApplicationEditStepFour';
import ApplicationEditNavbar from '../components/Application/EditeApplication/ApplicationEditNavbar';
import ApplicationEditStepTow from '../components/Application/EditeApplication/ApplicationEditStepTow';
import useApplicationDetails from '../hooks/useApplication';
import { SPACING, RADIUS } from '../theme/theme';
import { FormSkeleton } from '../components/Feedback/Loaders';
import Breadcrumb from '../components/Navigation/Breadcrumb';

interface ApplicationFormData {
  fa_name: string;
  en_name: string;
  fa_description: string;
  en_description: string;
  category: string;
  publisher_id: string;
  application_id: string;
  publish_after_accept: boolean | null;
  subtitle_fa: string; // فیلد جدید
  subtitle_en: string; // فیلد جدید
  logo: string | File | null;
  // `is_pending` marks a shot uploaded against an approved listing that the
  // store is not serving yet; it is carried down to the thumbnail that labels
  // it. The api always sends it, so leaving it off here only lost the type.
  screenshots: { id: number; url: string; is_pending?: boolean }[];
  screenshotFiles: File[];
  application_file?: File | null;
  application_file_url?: string | null;
}

// NOTE: theme.spacing is 1 in this app, so theme.spacing(3) was 3px, not 24px.
// These use the shared SPACING/RADIUS tokens instead.
const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: `${SPACING.md}px`,
  // Transparent so the grey page background separates the white panels.
  backgroundColor: 'transparent',
  // Sits inside Layout's <main> (a flex column): grow to fill it rather than
  // asking for a full 100vh, which would overflow by the header/footer height.
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    padding: `${SPACING.sm}px`,
  },
}));

const ContentWrapper = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: '900px',
  padding: `${SPACING.lg}px`,
  borderRadius: `${RADIUS.md}px`,
  overflow: 'hidden',
  boxShadow: theme.shadows[1],
  [theme.breakpoints.down('sm')]: {
    padding: `${SPACING.md}px`,
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: `${RADIUS.md}px`,
  overflow: 'hidden',
  padding: `${SPACING.md}px`,
  // boxShadow: theme.shadows[1],
  [theme.breakpoints.down('sm')]: {
    padding: `${SPACING.sm}px`,
  },
}));

const LoadingState = () => (
  <PageContainer>
    <ContentWrapper>
      {/* The page is a form, so mirror its shape while it loads. */}
      <FormSkeleton fields={5} />
    </ContentWrapper>
  </PageContainer>
);

const ErrorState = ({ error }: { error: unknown }) => (
  <PageContainer>
    <ContentWrapper>
      <Typography variant="h6" color="error" align="center">
        خطا:{' '}
        {error instanceof Error ? error.message : 'خطا در بارگذاری اپلیکیشن'}
      </Typography>
    </ContentWrapper>
  </PageContainer>
);

const EditApp: React.FC = () => {
  const location = useLocation();
  const { appId, publisherId } = location.state || {};

  const stepOneRef = useRef<{ submit: () => void }>(null);
  const stepThreeRef = useRef<{
    submit: () => void;
    getValues: () => {
      fa_description: string;
      en_description: string;
      subtitle_fa: string; // اضافه شد
      subtitle_en: string; // اضافه شد
    };
  }>(null);

  const [formData, setFormData] = useState<ApplicationFormData>({
    fa_name: '',
    en_name: '',
    fa_description: '',
    en_description: '',
    category: '',
    publisher_id: publisherId || '',
    application_id: appId || '',
    publish_after_accept: null,
    subtitle_fa: '', // اضافه شد
    subtitle_en: '', // اضافه شد

    logo: null,
    screenshots: [],
    screenshotFiles: [],
    application_file: null,
    application_file_url: null,
  });

  const [categories, setCategories] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(true);

  const { data, isLoading, isError, error } = useApplicationDetails(
    publisherId,
    appId,
  );

  const queryClient = useQueryClient();
  const [isDiscarding, setIsDiscarding] = useState<boolean>(false);

  /**
   * Throws away the edits waiting for review and returns the form to what the
   * store is serving. The api removes the staged text, logo and screenshots
   * together, so the page is refetched rather than patched locally.
   */
  const handleDiscardDraft = async () => {
    if (isDiscarding) {
      return;
    }

    setIsDiscarding(true);

    try {
      // Same shape as the unpublish call: ids in the query string, empty
      // body. ShowApplicationRequest reads input(), which covers both, but
      // matching the sibling endpoint leaves nothing to reason about.
      await apiClient.post('/v2/publisher/applications/discardDraft', null, {
        params: {
          publisher_id: publisherId,
          application_id: appId,
        },
      });

      await queryClient.invalidateQueries({
        queryKey: ['applicationDetails', publisherId, appId],
      });

      toast.success('تغییرات در انتظار بررسی لغو شد');
    } catch (err) {
      toast.error(extractApiError(err, 'خطا در لغو تغییرات'));
    } finally {
      setIsDiscarding(false);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await apiClient.get(
          '/v2/publisher/applications/category',
        );
        if (response.data && Array.isArray(response.data.categories)) {
          setCategories(response.data.categories);
        } else {
          toast.error('فرمت داده‌های دسته‌بندی‌ها نامعتبر است');
        }
      } catch (err: unknown) {
        if (err instanceof Error) {
          console.error('خطا در دریافت دسته‌بندی‌ها:', err);
          toast.error(err.message || 'خطا در دریافت دسته‌بندی‌ها');
        } else {
          console.error('خطای ناشناخته:', err);
          toast.error('خطا در دریافت دسته‌بندی‌ها');
        }
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (data?.application && categories.length > 0) {
      const {
        name_fa,
        name_en,
        description_fa,
        description_en,
        category_id, // استفاده از category_id به جای size_text
        logo,
        pending_logo,
        screenshots,
        subtitle_fa, // اگر از API میاد
        subtitle_en, // اگر از API میاد
        draft,
      } = data.application;

      // An edit to an approved listing is held for review rather than
      // published, so the fields above are still the live values. The form has
      // to show the pending one where there is one, or a publisher reopening
      // this page sees their change missing and makes it again.
      //
      // Per field, not all-or-nothing: `draft` carries only what actually
      // changed, so spreading it wholesale would blank everything else.
      const pending: Record<string, any> = draft ?? {};
      const staged = (column: string, live: any) =>
        column in pending ? pending[column] : live;

      setFormData((prev) => ({
        ...prev,
        fa_name: staged('name_fa', name_fa) || '',
        en_name: staged('name_en', name_en) || '',
        fa_description: staged('description_fa', description_fa) || '',
        en_description: staged('description_en', description_en) || '',
        category: staged('category_id', category_id)
          ? String(staged('category_id', category_id))
          : '', // تبدیل به string
        subtitle_fa: staged('subtitle_fa', subtitle_fa) || '', // اضافه شد
        subtitle_en: staged('subtitle_en', subtitle_en) || '', // اضافه شد
        // The pending logo is what the publisher last uploaded; `logo` is what
        // the store still serves until it is reviewed.
        logo: pending_logo || logo || null,
        screenshots: screenshots || [],
        screenshotFiles: [],
      }));
    }
  }, [data, categories]);

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Deletes a stored screenshot immediately, file and all. There is no undo,
   * so the confirmation lives in the component that owns the button.
   */
  const handleDeleteScreenshot = async (id: number) => {
    try {
      await apiClient.delete(
        `/v2/publisher/applications/screenShots/${id}`,
        {
          params: {
            publisher_id: formData.publisher_id,
            application_id: formData.application_id,
          },
        },
      );

      setFormData((prev) => ({
        ...prev,
        screenshots: prev.screenshots.filter((screen) => screen.id !== id),
      }));

      toast.success('تصویر حذف شد');
    } catch (error: any) {
      // The image stays on screen when the request fails, so the panel keeps
      // matching the server.
      toast.error(extractApiError(error, 'خطا در حذف تصویر'));
    }
  };

  const handleSubmit = async () => {
    stepOneRef.current?.submit();
    stepThreeRef.current?.submit();

    const stepThreeValues = stepThreeRef.current?.getValues();

    try {
      if (!formData.publisher_id || !formData.application_id) {
        toast.error('شناسه ناشر یا اپلیکیشن معتبر نیست');
        return;
      }

      // Every field the form exposes is required. Naming the empty ones
      // rather than saying "fill the required fields" saves the publisher
      // hunting across four steps for the one they missed.
      const missing = [
        [formData.fa_name, 'نام فارسی'],
        [formData.en_name, 'نام انگلیسی'],
        [formData.category, 'دسته‌بندی'],
        [stepThreeValues?.fa_description, 'توضیحات فارسی'],
        [stepThreeValues?.en_description, 'توضیحات انگلیسی'],
        [stepThreeValues?.subtitle_fa, 'زیرعنوان فارسی'],
        [stepThreeValues?.subtitle_en, 'زیرعنوان انگلیسی'],
      ]
        .filter(([value]) => !String(value ?? '').trim())
        .map(([, label]) => label);

      if (!formData.logo) {
        missing.push('آیکون اپلیکیشن');
      }

      // Either an already stored screenshot or a newly picked one counts.
      if (
        formData.screenshots.length === 0 &&
        formData.screenshotFiles.length === 0
      ) {
        missing.push('تصاویر اپلیکیشن');
      }

      if (missing.length > 0) {
        toast.error(`تکمیل این موارد الزامی است:\n${missing.join('\n')}`);
        return;
      }

      // بررسی طول توضیحات
      const faDescriptionLength = stepThreeValues?.fa_description?.length || 0;
      const enDescriptionLength = stepThreeValues?.en_description?.length || 0;

      if (faDescriptionLength > 10000) {
        toast.error(
          `طول توضیحات فارسی بیش از حد مجاز است (${faDescriptionLength}/10000)`,
        );
        return;
      }

      if (enDescriptionLength > 10000) {
        toast.error(
          `طول توضیحات انگلیسی بیش از حد مجاز است (${enDescriptionLength}/10000)`,
        );
        return;
      }

      const params = new URLSearchParams({
        publisher_id: formData.publisher_id,
        application_id: formData.application_id,
        fa_name: formData.fa_name,
        en_name: formData.en_name,
        category: formData.category,
        publish_after_accept: String(formData.publish_after_accept ?? 'null'),
      });

      const payload = new FormData();

      // اضافه کردن توضیحات به FormData به جای URL parameters
      payload.append('fa_description', stepThreeValues?.fa_description || '');
      payload.append('en_description', stepThreeValues?.en_description || '');
      payload.append('subtitle_fa', stepThreeValues?.subtitle_fa || '');
      payload.append('subtitle_en', stepThreeValues?.subtitle_en || '');

      if (formData.logo instanceof File) {
        payload.append('logo', formData.logo);
      } else if (formData.logo) {
        payload.append('logo_url', formData.logo);
      }

      if (formData.application_file_url) {
        payload.append('application_file_url', formData.application_file_url);
      }

      formData.screenshotFiles.forEach((file, index) => {
        payload.append(`screen_shots[${index}]`, file);
      });

      const response = await apiClient.post(
        `/v2/publisher/applications/update?${params.toString()}`,
        payload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            accept: 'application/json, text/plain, */*',
            'accept-language': 'en-US,en;q=0.9,fa;q=0.8,tr;q=0.7',
            origin: 'https://panel.sibche.com',
            referer: 'https://panel.sibche.com/',
          },
        },
      );

      if (response.status === 200) {
        toast.success('اپلیکیشن با موفقیت به‌روزرسانی شد');
      } else {
        toast.error(`خطا در به‌روزرسانی اپلیکیشن: ${response.statusText}`);
      }
    } catch (error: any) {
      console.error('خطا در به‌روزرسانی:', error);
      toast.error(extractApiError(error, 'خطا در به‌روزرسانی اپلیکیشن'));
    }
  };

  if (isLoading || categoriesLoading) return <LoadingState />;
  if (isError) return <ErrorState error={error} />;

  return (
    <>
      <CssBaseline />
      <PageContainer>
        {/* PageContainer centres a single child in a row, so a column wrapper
            is what lets the trail sit above the card and share its width. */}
        <Box
          sx={{
            width: '100%',
            maxWidth: '900px',
            display: 'flex',
            flexDirection: 'column',
            direction: 'rtl',
          }}
        >
          <Breadcrumb
            items={[
              { label: 'داشبورد', to: '/dashboard' },
              {
                label: 'اپلیکیشن‌ها',
                to: publisherId
                  ? `/applications?publisherId=${publisherId}`
                  : '/applications',
              },
              ...(appId
                ? [
                    {
                      label: formData.fa_name || formData.en_name || 'اپلیکیشن',
                      to: `/dashboard/application/${appId}`,
                      state: { appId, publisherId },
                    },
                  ]
                : []),
              { label: 'ویرایش اطلاعات' },
            ]}
          />

          

          {/* Shown while an edit is waiting to be reviewed. Without it the
              form looks like an ordinary edit screen, and a publisher cannot
              tell that what they are reading is not what the store serves. */}
          {data?.application?.has_pending_changes && (
            <Alert
              severity="warning"
              sx={{
                marginBottom: `${SPACING.md}px`,
                fontFamily: 'IRANYekanXFaNum',
                direction: 'rtl',
              }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={isDiscarding}
                  onClick={handleDiscardDraft}
                  sx={{ fontFamily: 'IRANYekanXFaNum' }}
                >
                  {isDiscarding ? 'در حال لغو…' : 'لغو تغییرات'}
                </Button>
              }
            >
              تغییرات شما ثبت شده و در انتظار بررسی است. تا زمان تایید، نسخه‌ی
              فعلی روی سیبچه نمایش داده می‌شود.
            </Alert>
          )}

          <ContentWrapper elevation={3}>
            {/* Heading row lives beside the breadcrumb, above the card. */}
          <Box sx={{ marginBottom: `${SPACING.md}px` }}>
            <ApplicationEditNavbar onSubmit={handleSubmit} />
          </Box>

          <Grid container spacing={SPACING.md}>
            {/* `spacing` on a nested `container item` applies MUI's negative
                margin a second time, so the row hung 16px past the wrapper.
                A plain gap spaces the two cards without that side effect. */}
            <Grid container item sx={{ gap: `${SPACING.md}px` }}>
              {/* flex rather than md={6}: with a gap in play, two 50% columns
                  no longer fit on one row. */}
            
              
              <Grid item xs={12} sx={{ flex: { md: 1 }, minWidth: 0 }}>
                <FormSection>
                  <ApplicationEditStepOne
                    ref={stepOneRef}
                    fa_name={formData.fa_name}
                    en_name={formData.en_name}
                    category={formData.category}
                    onFieldChange={handleFieldChange}
                    id={formData.application_id}
                    display_name={formData.fa_name}
                  />
                </FormSection>
              </Grid>

                <Grid item xs={12} sx={{ flex: { md: 1 }, minWidth: 0 }}>
                <FormSection>
                  <ApplicationEditStepTow
                    publish_after_accept={formData.publish_after_accept}
                    onFieldChange={handleFieldChange}
                  />
                </FormSection>
              </Grid>
              
            </Grid>
            <Grid item xs={12}>
              <FormSection>
                <ApplicationEditStepThree
                  ref={stepThreeRef}
                  en_description={formData.en_description}
                  fa_description={formData.fa_description}
                  subtitle_fa={formData.subtitle_fa} // اضافه شد
                  subtitle_en={formData.subtitle_en} // اضافه شد
                  onFieldChange={handleFieldChange}
                />
              </FormSection>
            </Grid>
            <Grid item xs={12}>
              <FormSection>
                <ApplicationEditStepFour
                  logo={formData.logo}
                  // Only while the logo on screen is the stored pending one.
                  // Once the publisher picks a new file `formData.logo` is a
                  // File and has not been sent yet, so it is not "awaiting
                  // review" — labelling it then would be wrong.
                  logoPending={
                    !!data?.application?.pending_logo &&
                    !(formData.logo instanceof File)
                  }
                  screenshots={formData.screenshots}
                  onLogoChange={(file) => handleFieldChange('logo', file)}
                  // Newly picked files only. `screenshots` holds what is
                  // already stored and must not be overwritten with previews
                  // of the new picks, or saving would look like a replacement.
                  onAddScreenshots={(files) => {
                    handleFieldChange('screenshotFiles', files);
                  }}
                  onRemoveScreenshot={(index) => {
                    handleFieldChange(
                      'screenshotFiles',
                      formData.screenshotFiles.filter((_, i) => i !== index),
                    );
                  }}
                  onDeleteScreenshot={handleDeleteScreenshot}
                />
              </FormSection>
            </Grid>
          </Grid>
          </ContentWrapper>
        </Box>
      </PageContainer>
    </>
  );
};

export default EditApp;
